import { useTranslate } from 'react-admin';
import { Box, Card, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import type { RedemptionRecord } from 'src/pages/admin/services/vaultService';

import { LongId, NUMERIC, Stacked, VaultCell } from './cells';
import { RECORDS_NS as NS } from './constants';
import { redemptionCells, redemptionStatus } from './helpers';

/** PRD §14.6's fields, in its order, plus feeForgone beside the fee it was taken from (XBIT-4311). */
const COLUMNS = [
  'orderId',
  'vault',
  'user',
  'requested',
  'x',
  'recovered',
  'slippage',
  'fee',
  'feeForgone',
  'net',
  'status',
  'time',
] as const;

export const RedemptionsTable = ({ records }: { records: RedemptionRecord[] }) => {
  const translate = useTranslate();

  if (records.length === 0) {
    return (
      <Card sx={{ p: 3 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {translate(`${NS}.empty`)}
        </Typography>
      </Card>
    );
  }

  return (
    <Card>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1280 }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell key={c} sx={{ whiteSpace: 'normal', verticalAlign: 'bottom' }}>
                  {translate(`${NS}.redemptions.${c}`)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((r) => {
              const cells = redemptionCells(r);
              const status = redemptionStatus(r.status, translate);
              return (
                <TableRow key={r.id}>
                  <TableCell sx={{ minWidth: 160 }}>
                    <LongId value={cells.orderId} head={10} tail={6} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <VaultCell name={r.vaultName} vaultId={r.vaultId} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <LongId value={r.holderId} head={8} tail={6} />
                  </TableCell>
                  <TableCell sx={NUMERIC}>{cells.requested}</TableCell>
                  <TableCell sx={NUMERIC}>{cells.x}</TableCell>
                  <TableCell sx={NUMERIC}>{cells.recovered}</TableCell>
                  <TableCell sx={NUMERIC}>{cells.slippage}</TableCell>
                  <TableCell sx={NUMERIC}>{cells.fee}</TableCell>
                  <TableCell sx={NUMERIC}>{cells.feeForgone}</TableCell>
                  <TableCell sx={NUMERIC}>{cells.net}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" color={status.color} label={status.label} />
                    {cells.reason && (
                      <Typography
                        variant="caption"
                        component="div"
                        sx={{ color: 'text.secondary', mt: 0.5, fontFamily: 'monospace' }}
                      >
                        {cells.reason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stacked
                      main={cells.issuedAt}
                      note={r.executedAt ? translate(`${NS}.paidAt`, { at: cells.executedAt }) : cells.executedAt}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
};
